import clsx from "clsx";
import React, { useCallback, useRef } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";

import AlertContainer from "../components/common/AlertContainer";
import { AutocompleteFix } from "../components/common/AutocompleteFix";
import { ClubBenefits } from "../components/common/ClubBenefits";
import { LoginLink } from "../components/common/LoginLink";
import SpinningCircle from "../components/common/SpinningCircle";
import { VippsButton } from "../components/common/VippsButton";
import { Consents } from "../components/consents/Consents";
import { ConsentsContainer } from "../components/consents/ConsentsContainer";
import { Checkbox } from "../components/controls/Checkbox";
import { Email } from "../components/controls/Email";
import { Password } from "../components/controls/Password";
import { Phone } from "../components/controls/Phone";
import { Postcode } from "../components/controls/Postcode";
import { SubmitButton } from "../components/controls/SubmitButton";
import { Text } from "../components/controls/Text";
import CustomerConditions from "../components/modals/CustomerConditions";
import Newsletter from "../components/modals/Newsletter";
import { useFieldValue } from "../hooks/useFieldValue";
import {
	registerUser,
	validatePostcodeLookup,
} from "../modules/registration/slices/register.actions";
import {
	showConditionsModal,
	showNewsletterModal,
	handleFieldValueChange,
	onAlertClose,
	hideModal,
	resetEmailLookup,
} from "../modules/registration/slices/register.slice";
import { TYPE_DEBOUNCE } from "../modules/settings/common.utils";
import { castToBoolean, normalizeValue } from "../utils/helpers";
import { STORE_TYPE, STORES_ID } from "../utils/stores";
import { trackEvent } from "../utils/tracking";
import { TRACKING_EVENTS } from "../utils/trackingDefinitions";

import { RULES, getFieldsConfiguration } from "./RegistrationContainer.fields";

export const RegistrationContainer = () => {
	const dispatch = useDispatch();
	const { formatMessage } = useIntl();

	const {
		handleSubmit,
		register,
		setValue,
		getValues,
		clearErrors,
		trigger,
		formState: { errors },
	} = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
	});

	const alerts = useSelector((state) => state.registration.alerts);

	const email = useSelector((state) => state.registration.b2c.form.email);
	const passedEmail = useSelector(
		(state) => state.registration.b2c.form.passedEmail,
	);
	const phone = useSelector((state) => state.registration.b2c.form.phone);
	const name = useSelector((state) => state.registration.b2c.form.name);
	const password = useSelector((state) => state.registration.b2c.form.password);
	const postcode = useSelector((state) => state.registration.b2c.form.postcode);
	const city = useSelector((state) => state.registration.b2c.form.city);

	const acceptWeeklyNewsletter = useSelector(
		(state) => state.registration.b2c.acceptWeeklyNewsletter,
	);

	const acceptEmailNotifications = useSelector(
		(state) => state.registration.b2c.acceptEmailNotifications,
	);

	const acceptSMSNotifications = useSelector(
		(state) => state.registration.b2c.acceptSMSNotifications,
	);

	const acceptCustomerConditions = useSelector(
		(state) => state.registration.b2c.acceptCustomerConditions,
	);

	const addressLine1 = useSelector(
		(state) => state.registration.b2c.form.addressLine1,
	);

	const returnUrl = useSelector(
		(state) => state.registration.b2c.form.returnUrl,
	);

	const isConditionsModalOpen = useSelector(
		(state) => state.registration.showConditionsModal,
	);

	const isNewsletterModalOpen = useSelector(
		(state) => state.registration.showNewsletterModal,
	);

	const isCheckingUsername = useSelector(
		(state) => state.registration.b2c.isCheckingUsername,
	);
	const savedUsername = useSelector(
		(state) => state.registration.b2c.savedUsername,
	);
	const usernameExists = useSelector(
		(state) => state.registration.b2c.usernameExists,
	);

	const isPrivateConsentsEnabled = useSelector(
		(state) => state.settings.features.privateConsents,
	);

	const acceptSalesConditions = useSelector(
		(state) => state.registration.b2c.form.acceptSalesConditions,
	);

	const acceptSmsNotifications = useSelector(
		(state) => state.registration.b2c.form.acceptSmsNotifications,
	);

	const acceptClubAgreementConsents = useSelector(
		(state) => state.registration.b2c.form.acceptClubAgreementConsents,
	);

	const enableClubAgreementConsent = useSelector(
		(state) => state.settings.features.enableClubAgreementConsent,
	);

	const isFetching = useSelector((state) => state.registration.isFetching);

	const useConsents = useSelector(
		(state) => state.settings.features.useConsents,
	);

	const areaCode = useSelector((state) => state.settings.areaCode);
	const country = useSelector((state) => state.settings.country);
	const storeId = useSelector((state) => state.settings.storeId);

	const validationRules = useSelector(
		(state) => state.settings.validationRules,
	);

	const identityEnableVippsLogin = useSelector(
		(state) => state.settings.features.identityEnableVippsLogin,
	);

	const vippsLoginLink = useSelector((state) => state.settings.vippsLoginLink);

	const isRegisteringNewUser = useSelector(
		(state) => state.registration.isRegisteringNewUser,
	);

	const isCrossCustomerStore = storeId === STORES_ID["321"];

	const FORM_FIELDS = getFieldsConfiguration({
		dispatch,
		register,
		errors,
		values: {
			phone,
			name,
			address: addressLine1,
			postcode,
			city,
			email: passedEmail || email,
			password,
			acceptWeeklyNewsletter,
			acceptCustomerConditions,
			acceptEmailNotifications,
			acceptSMSNotifications,
			validationRules,
		},
		options: {
			isFetching,
			useConsents,
			identityEnableVippsLogin,
			isPrivateConsentsEnabled,
			isActive: true,
			isCheckingUsername,
			savedUsername,
			usernameExists,
		},
	});

	const formProps = {
		register,
		setValue,
		getValues,
		trigger,
		errors,
		FORM_FIELDS,
	};

	const handlePostcodeLookup = useDebouncedCallback(({ target: { value } }) => {
		if (value.trim()) {
			dispatch(
				validatePostcodeLookup({
					postcode: value,
					countryCode: country,
					form: "b2c",
					target: "city",
				}),
			).then((response) => {
				setValue("city", response.payload?.result, { shouldValidate: true });
			});
		}
	}, TYPE_DEBOUNCE);

	const handleDebounceFieldValidation = useDebouncedCallback(async (id) => {
		await trigger(id);
	}, TYPE_DEBOUNCE);

	const handleDispatchFieldChange = useCallback(
		async ({ id, value, type }) => {
			if (id) {
				if (["radio", "checkbox"].includes(type)) {
					await trigger(id);
				} else {
					value ? clearErrors(id) : await trigger(id);
				}

				dispatch(
					handleFieldValueChange({
						form: "b2c",
						field: id,
						value: normalizeValue(value),
					}),
				);

				await handleDebounceFieldValidation(id);
				setValue(id, value);
			}
		},
		[trigger, setValue, clearErrors, handleDebounceFieldValidation, dispatch],
	);

	const handleFieldChange = async ({ target }) => {
		await handleDispatchFieldChange(target);
	};

	const handleRegister = async (values) => {
		dispatch(onAlertClose([]));
		dispatch(resetEmailLookup());

		await trigger(["email"]);

		if (usernameExists) {
			return;
		}

		const validationFields = ["name", "email", "phone", "password"];

		if (isPrivateConsentsEnabled) {
			if (enableClubAgreementConsent) {
				validationFields.push(
					"acceptClubAgreementConsents",
					"acceptSmsNotifications",
				);
			} else {
				validationFields.push(
					"acceptMarketingConsents",
					"accept3rdPartyMarketingConsents",
					"acceptSmsNotifications",
				);
			}
		}

		let payload = {
			name: (values.name || "").trim(),
			email: (values.email || "").trim(),
			mobileNumber: (values.phone || "").trim(),
			password: values.password,
			addressLine: (values.addressLine1 || "").trim(),
			postalCode: (values.postcode || "").trim(),
			city: (values.city || "").trim(),
			country,
			returnUrl,
			consentsInformationText: formatMessage({
				id: "gdpr.consents.information.modal.info",
			}),
			acceptCustomerConditions: !useConsents
				? values.acceptCustomerConditions
				: true,
			acceptSalesConditions:
				!useConsents ||
				values.acceptSalesConditions ||
				identityEnableVippsLogin ||
				false,
			acceptWeeklyNewsletter: values.acceptWeeklyNewsletter || false,
			acceptEmailNotifications: castToBoolean(values.acceptEmailNotifications),
			acceptSMSNotifications: castToBoolean(values.acceptSMSNotifications),
			priceList: (values.priceList || "").trim(),
			consents: {},
		};

		if (isPrivateConsentsEnabled && enableClubAgreementConsent) {
			payload = Object.assign(payload, {
				consents: {
					clubAgreement: acceptClubAgreementConsents,
					clubAgreementConsentText: formatMessage({
						id: "consents.clubAgreement.text",
					}),
					clubSms: acceptSmsNotifications,
					clubSmsConsentText: formatMessage({
						id: "consents.clubSms.text",
					}),
				},
			});
		}

		const isValid = await trigger(validationFields);

		if (isValid) {
			dispatch(registerUser(payload));
		} else {
			Object.entries(errors).forEach(([field]) => {
				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.VALIDATION_ERROR,
					target: TRACKING_EVENTS.REGISTRATION.APP,
					value: field,
				});
			});
		}
	};

	const fieldRef = useRef(false);
	useFieldValue({
		fieldRef,
		fields: [
			{ name: "name", value: name },
			{ name: "city", value: city },
			{ name: "addressLine1", value: addressLine1 },
			{ name: "postcode", value: postcode },
		],
		setValue,
		updateFieldInStore: handleDispatchFieldChange,
		condition: name,
	});

	return (
		<>
			<AlertContainer
				alerts={alerts}
				onAlertClose={(data) => dispatch(onAlertClose(data))}
			/>

			<div
				className={clsx({
					"form-wrapper": true,
					extended: isPrivateConsentsEnabled,
					club: isPrivateConsentsEnabled,
				})}
			>
				{isPrivateConsentsEnabled && <ClubBenefits target="sidebar" />}
				<FormProvider {...formProps}>
					<section
						key="registration"
						name="section-register-user"
						className="form-section"
					>
						<h1>
							{formatMessage({
								id: "komid.register.open.account",
							})}
						</h1>

						<h3>
							{formatMessage({ id: "komid.register.already.registered" })}{" "}
							<a
								href={`/login/local?returnUrl=${encodeURIComponent(returnUrl)}`}
							>
								{formatMessage({ id: "komid.login.form.header" })}
							</a>
						</h3>
						{identityEnableVippsLogin && (
							<div className="form-text-spacer" style={{ paddingBottom: 0 }}>
								<VippsButton href={vippsLoginLink} target="registration" />
								<div className="separator">
									<span>
										{formatMessage({ id: "komid.login.separatortext" })}
									</span>
								</div>
							</div>
						)}
						<form
							onSubmit={handleSubmit(
								(data) => handleRegister(data),
								(errors) => {
									console.log(errors);
								},
							)}
							data-hj-suppress
						>
							<fieldset name="registerUser">
								<AutocompleteFix />

								<Text
									id="name"
									name="name"
									className="form-input"
									ref={FORM_FIELDS.NAME.FIELD.ref}
									error={FORM_FIELDS.NAME.ERROR}
									value={name}
									label={formatMessage({ id: "komid.register.name" })}
									placeholder={formatMessage({
										id: "komid.register.name.placeholder",
									})}
									maxLength={RULES.REGISTRATION_FIELDS_LENGTH.NAME}
									onChange={handleFieldChange}
									onBlur={(event) => {
										if (name !== event.target.value) {
											handleFieldChange(event);
										}
									}}
									autoFocus
									autoComplete="name"
								/>

								<Text
									id="addressLine1"
									name="addressLine1"
									className="form-input"
									ref={FORM_FIELDS.ADDRESS.FIELD.ref}
									error={FORM_FIELDS.ADDRESS.ERROR}
									value={addressLine1}
									label={formatMessage({ id: "komid.register.address" })}
									maxLength={RULES.REGISTRATION_FIELDS_LENGTH.ADDRESS}
									autoComplete="address-line1"
									onChange={handleFieldChange}
									onBlur={(event) => {
										if (addressLine1 !== event.target.value) {
											handleFieldChange(event);
										}
									}}
								/>

								<div className="form-row">
									<div
										className="form-column"
										style={{
											flex: "1 1 40%",
											padding: "0 16px 0 0",
										}}
									>
										<Postcode
											id="postcode"
											name="postcode"
											key="postcode"
											className="form-input"
											target="city"
											ref={FORM_FIELDS.POSTCODE.FIELD.ref}
											error={FORM_FIELDS.POSTCODE.ERROR}
											value={postcode}
											onChange={(event) => {
												handleFieldChange(event);
												handlePostcodeLookup(event);
											}}
											onBlur={(event) => {
												if (postcode !== event.target.value) {
													handleFieldChange(event);
												}
											}}
											label={formatMessage({ id: "komid.register.postcode" })}
											autoComplete="postal-code"
										/>
									</div>

									<div
										className="form-column"
										style={{
											flex: "1 1 60%",
										}}
									>
										<Text
											id="city"
											name="city"
											className="form-input"
											ref={FORM_FIELDS.CITY.FIELD.ref}
											error={FORM_FIELDS.CITY.ERROR}
											value={city}
											disabled
											label={formatMessage({ id: "komid.register.city" })}
											maxLength={RULES.REGISTRATION_FIELDS_LENGTH.CITY}
											onChange={handleFieldChange}
										/>
									</div>
								</div>

								<Phone
									id="phone"
									name="phone"
									className="form-input"
									ref={FORM_FIELDS.PHONE.FIELD.ref}
									error={FORM_FIELDS.PHONE.ERROR}
									value={phone}
									placeholder={formatMessage({
										id: "komid.register.mobilephone.placeholder",
									})}
									maxLength={validationRules.mobilePhoneLength}
									label={formatMessage({ id: "komid.register.mobilephone" })}
									autoComplete="tel-national"
									areaCode={areaCode}
									onChange={handleFieldChange}
									onBlur={(event) => {
										if (phone !== event.target.value) {
											handleFieldChange(event);
										}
									}}
									withSpinner={isFetching}
								/>

								<Email
									id="email"
									className="form-input"
									name="email"
									ref={FORM_FIELDS.EMAIL.FIELD.ref}
									error={FORM_FIELDS.EMAIL.ERROR}
									loginLink={
										FORM_FIELDS.EMAIL.ERROR && <LoginLink form="b2c" />
									}
									value={email}
									maxLength={RULES.REGISTRATION_FIELDS_LENGTH.EMAIL}
									validationRules={validationRules}
									autoComplete="new-password"
									autoFocus={false}
									label={formatMessage({ id: "komid.register.email" })}
									placeholder={formatMessage({
										id: "komid.register.email.placeholder",
									})}
									onChange={handleFieldChange}
									withIcon
								/>

								<Password
									id="password"
									ref={FORM_FIELDS.PASSWORD.FIELD.ref}
									error={FORM_FIELDS.PASSWORD.ERROR}
									className="form-input"
									label={formatMessage({
										id: "komid.register.password",
									})}
									onChange={handleFieldChange}
									autoComplete="new-password"
									autoFocus
									type="password"
									placeholder="******"
									maxLength={RULES.REGISTRATION_FIELDS_LENGTH.PASSWORD_MAX}
								/>

								{isPrivateConsentsEnabled ? (
									<>
										<Checkbox
											id="acceptSalesConditions"
											name="acceptSalesConditions"
											ref={FORM_FIELDS.ACCEPT_SALES_CONDITIONS.FIELD.ref}
											error={FORM_FIELDS.ACCEPT_SALES_CONDITIONS.ERROR}
											value={acceptSalesConditions}
											className="form-input"
											label={
												formatMessage({ id: "profile.SalesConditionsAccept" }) +
												" "
											}
											linkText={formatMessage({
												id: "profile.SalesConditionsAcceptLinkText",
											})}
											onChange={handleFieldChange}
											onLinkClick={() => dispatch(showConditionsModal())}
										/>
										<Consents
											onFieldChange={handleFieldChange}
											target={STORE_TYPE.B2C}
										/>
									</>
								) : useConsents ? (
									<ConsentsContainer onFieldChange={handleFieldChange} />
								) : (
									<>
										<Checkbox
											name="acceptWeeklyNewsletter"
											id="acceptWeeklyNewsletter"
											ref={FORM_FIELDS.ACCEPT_WEEKLY_NEWSLETTER.FIELD.ref}
											error={FORM_FIELDS.ACCEPT_WEEKLY_NEWSLETTER.ERROR}
											value={acceptWeeklyNewsletter}
											className="form-input"
											label={formatMessage({
												id: "komid.register.weeklyOffer.label",
											})}
											linkText={formatMessage({
												id: "komid.register.weeklyOffer.readMore",
											})}
											onChange={handleFieldChange}
											onLinkClick={() => dispatch(showNewsletterModal())}
										/>

										<Checkbox
											name="acceptCustomerConditions"
											id="acceptCustomerConditions"
											ref={FORM_FIELDS.ACCEPT_CUSTOMER_CONDITIONS.FIELD.ref}
											error={FORM_FIELDS.ACCEPT_CUSTOMER_CONDITIONS.ERROR}
											value={acceptCustomerConditions}
											className="form-input"
											label={formatMessage({
												id: "komid.register.customerconditions",
											})}
											linkText={formatMessage({
												id: "komid.register.customerconditions.link",
											})}
											onChange={handleFieldChange}
											onLinkClick={() => dispatch(showConditionsModal())}
										/>
									</>
								)}
							</fieldset>

							{isPrivateConsentsEnabled && <ClubBenefits target="form" />}

							<SubmitButton
								onClick={handleSubmit(
									(data) => handleRegister(data),
									(errors) => {
										console.log(errors);
									},
								)}
								disabled={isRegisteringNewUser}
								data-automationid="registerUser.submitButton"
								data-testid="registerUser.submitButton"
							>
								{formatMessage({ id: "komid.register.submit" })}
								{isRegisteringNewUser && <SpinningCircle className="loader" />}
							</SubmitButton>
						</form>
						<CustomerConditions
							isOpen={isConditionsModalOpen}
							close={() => dispatch(hideModal())}
							isBusiness={false}
						/>
						<Newsletter
							isOpen={isNewsletterModalOpen}
							close={() => dispatch(hideModal())}
						/>
						{isCrossCustomerStore && (
							<div className="form-bottom-links">
								<p>
									{formatMessage({
										id: "komid.register.form.business.customer",
									})}
								</p>
								<a
									data-testid="register.private.form.register.business.user.link"
									href={`/register/business?returnUrl=${encodeURIComponent(
										returnUrl,
									)}`}
								>
									{formatMessage({
										id: "komid.register.form.business.customer.register.link",
									})}
								</a>
							</div>
						)}
					</section>
					{isPrivateConsentsEnabled && <div className="club-spacer" />}
				</FormProvider>
			</div>
		</>
	);
};
